import React from 'react'
import { FormattedMessage } from 'react-intl'
import FormattedCurrency from '../FormattedCurrency'
import messages from './messages'

type duration = 1 | 3 | 6 | 12 | 24

type RegularPricingMessageProps = {
  duration: duration
  price: number
  currency: string
}

const RegularPricingMessage = ({
  duration,
  price,
  currency,
}: RegularPricingMessageProps) => {
  const message = {
    1: messages['upgrade.billed_monthly'],
    3: messages['upgrade.billed_quarterly'],
    6: messages['upgrade.billed_twiceAYear'],
    12: messages['upgrade.billed_yearly'],
    24: messages['upgrade.billed_everyOtherYear'],
  }[duration]

  return (
    <FormattedMessage
      {...message}
      values={{
        price: <FormattedCurrency value={price} currency={currency} round />,
      }}
    />
  )
}

type PriceChangeProps = {
  price: number
  originalPrice: number
  currency: string
  roundDiscount: boolean
}

const PriceChange = ({
  price,
  originalPrice,
  currency,
  roundDiscount,
}: PriceChangeProps) => (
  <>
    <del className="line-through">
      <FormattedCurrency value={originalPrice} currency={currency} round />
    </del>{' '}
    <ins>
      <FormattedCurrency
        value={price}
        currency={currency}
        round={roundDiscount}
      />
    </ins>
  </>
)

type DiscountMessageProps = {
  duration: duration
  price: number
  currency: string
  originalPrice: number
  roundDiscount: boolean
  discountOnlyFirstPurchase: boolean
}

const DiscountMessage = ({
  duration,
  price,
  originalPrice,
  currency,
  roundDiscount,
  discountOnlyFirstPurchase,
}: DiscountMessageProps) => {
  const message = {
    1: messages['upgrade.billed_first_month'],
    3: messages['upgrade.billed_first_quarter'],
    6: messages['upgrade.billed_first_half_year'],
    12: messages['upgrade.billed_first_year'],
    24: messages['upgrade.billed_first_two_years'],
  }[duration]

  return discountOnlyFirstPurchase ? (
    <FormattedMessage
      {...message}
      values={{
        price: (
          <PriceChange
            price={price}
            originalPrice={originalPrice}
            currency={currency}
            roundDiscount={roundDiscount}
          />
        ),
      }}
    ></FormattedMessage>
  ) : (
    <PriceChange
      price={price}
      originalPrice={originalPrice}
      currency={currency}
      roundDiscount={roundDiscount}
    />
  )
}

type DiscountedProps = {
  duration: duration
  discount: number
  price: number
  originalPrice: number
  currency: string
  roundDiscount: boolean
  discountOnlyFirstPurchase: boolean
}

type NonDiscountedProps = {
  duration: duration
  price: number
  currency: string
  discount?: never
  originalPrice?: never
  roundDiscount?: true | never
  discountOnlyFirstPurchase?: boolean
}

type Props = DiscountedProps | NonDiscountedProps

const BillingText = ({
  duration,
  discount,
  price,
  originalPrice,
  currency,
  roundDiscount = true,
  discountOnlyFirstPurchase = true,
}: Props) =>
  discount && originalPrice ? (
    <DiscountMessage
      {...{
        duration,
        price,
        originalPrice,
        discount,
        currency,
        roundDiscount,
        discountOnlyFirstPurchase,
      }}
    />
  ) : (
    <RegularPricingMessage {...{ duration, price, currency }} />
  )

export default BillingText
